/**
 * The Vera people directory: every human who can sign in to Vera, with the
 * composite id the BFF session actually carries.
 *
 * WHY THIS EXISTS IN @shared. Portfolio and Share each grew their own copy of
 * this two-source join (`packages/portfolio/src/lib/PeoplePicker.tsx`,
 * `packages/share/src/lib/directory.ts`). Academy Phase 4a would have been the
 * third, so it lives here instead. Those two are NOT yet migrated onto it:
 * they are shipped and deployed, and the Share variant additionally annotates
 * each person with their roles, so folding them in is its own change. See the
 * ROADMAP line.
 *
 * The join, and why it is two sources:
 *   - `systemusers` for REAL display names (fullname) and UPNs. UPN-derived
 *     name guessing is wrong for this tenant's first-initial UPNs
 *     ('sballen@...' is Sasha Ballen, not "Sballen"), so fullname wins.
 *   - `vba_userroleassignment` for the composite `<oid>.<homeTenantId>` ids the
 *     BFF session carries (see the userobjectid contract). Anything keyed on a
 *     bare oid silently fails to match a real session.
 *
 * There are TWO distinct flavours of "has not signed in yet", and the join
 * has to answer both or a new hire is unreachable:
 *
 *   1. IN systemusers, no grant row. Licensed in Dataverse, so their real
 *      Entra oid is known; the composite is CONSTRUCTED from the shared home
 *      tenant id. Flagged `hasNoRoles`.
 *
 *   2. NEITHER source has a usable id: the person exists only as
 *      pre-provisioned (pending) vba_userroleassignment rows, which carry an
 *      email and a NULL oid, and Dataverse has never seen them. This is what
 *      an admin creates when they grant roles to a hire by email. No oid
 *      exists anywhere we are allowed to read (Graph User.Read.All is the
 *      consent Phase 4b waits on), so they get the synthetic
 *      `pending:<email>` id and are flagged `isPending`. The BFF re-keys
 *      their rows onto the real composite on first sign-in.
 *
 * Case 2 used to fall out of the list entirely, which is how a hire with
 * thirteen pre-provisioned role grants could still be unassignable.
 */

import { useQuery } from '@tanstack/react-query';
import { dataverseClient } from '../../../api';
import { userRoleAssignmentApi } from '../../user-role-assignment';
import { pendingPersonKey, prettyNameFromUpn } from './pendingPerson';

export { PENDING_PERSON_PREFIX, pendingPersonKey, prettyNameFromUpn } from './pendingPerson';

export interface VeraDirectoryPerson {
  /** Composite `<oid>.<homeTenantId>`, matching the BFF session oid - or
   *  `pending:<email>` when `isPending`, because no oid exists yet. */
  oid: string;
  upn: string;
  name: string;
  /** True when the person has no role grant row at all (never signed in / never granted). */
  hasNoRoles: boolean;
  /** True when the person exists ONLY as pre-provisioned grants: no Dataverse
   *  record, so no real oid until their first sign-in. */
  isPending: boolean;
}

interface SystemUserEntity {
  systemuserid: string;
  fullname?: string;
  domainname?: string;
  azureactivedirectoryobjectid?: string;
  [k: string]: unknown;
}

/**
 * Guest / B2B accounts are EXCLUDED (Sasha, 2026-08-18, extending the ADO
 * #1498 decision from Portfolio to here). Everything this directory feeds is
 * an internal accountability: who owns an initiative, who is assigned a
 * course. Offering hundreds of external accounts in those pickers is how the
 * wrong person ends up attached to one.
 *
 * `#EXT#` in the UPN is Entra's marker for an invited B2B account, and it is
 * what Dataverse stores in `domainname` for them. Reliable but not total: a
 * guest later converted to a full member keeps a normal-looking UPN. Catching
 * that needs a usertype the systemusers entity does not carry, so it needs
 * Graph (the User.Read.All consent Phase 4b is still waiting on), which is the
 * same reason the pending-person case below exists at all.
 */
const isGuestUpn = (upn: string) => upn.toUpperCase().includes('#EXT#');

/**
 * Delegated Dataverse reads, so only render this behind a gated surface. The
 * 10-minute staleTime matches the portfolio picker: the roster barely moves.
 */
export function useVeraDirectory(enabled = true) {
  return useQuery({
    queryKey: ['shared', 'vera-directory'],
    enabled,
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<VeraDirectoryPerson[]> => {
      const [assignments, usersRes] = await Promise.all([
        userRoleAssignmentApi.list(),
        dataverseClient.list<SystemUserEntity>('systemusers', {
          select: ['fullname', 'domainname', 'azureactivedirectoryobjectid'],
          // Humans only: application users carry applicationid; SYSTEM and
          // INTEGRATION rows have no Entra object id.
          filter:
            'applicationid eq null and isdisabled eq false and azureactivedirectoryobjectid ne null',
          orderBy: 'fullname asc',
        }),
      ]);

      // The shared home tenant id, read off any granted user's composite id.
      const anyComposite = assignments.find((a) => a.userObjectId.includes('.'))?.userObjectId ?? '';
      const tenantId = anyComposite.slice(anyComposite.indexOf('.') + 1);

      const grantedComposites = new Set<string>();
      const compositeByBareOid = new Map<string, string>();
      for (const a of assignments) {
        if (!a.userObjectId) continue;
        grantedComposites.add(a.userObjectId);
        const dot = a.userObjectId.indexOf('.');
        if (dot > 0) {
          compositeByBareOid.set(a.userObjectId.slice(0, dot).toLowerCase(), a.userObjectId);
        }
      }

      const byOid = new Map<string, VeraDirectoryPerson>();
      for (const u of usersRes.value) {
        const bare = u.azureactivedirectoryobjectid ?? '';
        if (!bare) continue;
        const upn = u.domainname ?? '';
        if (isGuestUpn(upn)) continue;
        const oid =
          compositeByBareOid.get(bare.toLowerCase()) ?? (tenantId ? `${bare}.${tenantId}` : bare);
        if (byOid.has(oid)) continue;
        byOid.set(oid, {
          oid,
          upn,
          name: u.fullname || (upn ? prettyNameFromUpn(upn) : bare),
          hasNoRoles: !grantedComposites.has(oid),
          isPending: false,
        });
      }

      // Granted users somehow absent from systemusers still appear (UPN name),
      // guests excepted: this pass would otherwise let back in exactly the
      // accounts the one above filtered out.
      for (const a of assignments) {
        if (!a.userObjectId || !a.userPrincipalName || byOid.has(a.userObjectId)) continue;
        if (isGuestUpn(a.userPrincipalName)) continue;
        byOid.set(a.userObjectId, {
          oid: a.userObjectId,
          upn: a.userPrincipalName,
          name: prettyNameFromUpn(a.userPrincipalName),
          hasNoRoles: false,
          isPending: false,
        });
      }

      // Pre-provisioned people: pending rows (email, NULL oid). One person
      // holds one row per granted role, so dedupe by email, and skip anyone
      // already visible above - a half-reconciled user must not appear twice,
      // once real and once pending.
      const seenUpns = new Set(
        [...byOid.values()].map((p) => p.upn.toLowerCase()).filter(Boolean)
      );
      for (const a of assignments) {
        if (a.userObjectId) continue;
        const upn = a.userPrincipalName?.trim().toLowerCase();
        if (!upn || seenUpns.has(upn)) continue;
        // Same guest rule, though it bites less here: a pending row is an
        // email an admin typed, so an external one arrives as a plain address
        // with no #EXT# marker to catch. That case stays possible, and it is
        // a deliberate admin act rather than the directory volunteering
        // hundreds of strangers, which is what this decision was about.
        if (isGuestUpn(upn)) continue;
        const oid = pendingPersonKey(upn);
        if (byOid.has(oid)) continue;
        byOid.set(oid, {
          oid,
          upn,
          // Deliberately the email, not a name guessed from it: this tenant's
          // UPNs are first-initial + surname, so guessing yields "Laspenwall".
          // The email is at least exact, and the BFF replaces it with the real
          // display name the moment they sign in.
          name: upn,
          hasNoRoles: false,
          isPending: true,
        });
      }

      return [...byOid.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}
